import React, {useCallback, useContext, useDeferredValue, useEffect, useMemo, useState} from 'react';
import {
    FlatList,
    Image,
    Keyboard,
    Modal,
    Platform,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { Directions, Gesture, GestureDetector } from 'react-native-gesture-handler';
import {searchFoodByKeywords} from '../components/PairingModal'
import {findFoodsByCode} from '../helpers/foodKeywordSearch';
import {buildCartCountByItemId, getCartCount} from '../helpers/cartItemCounts';
import {OnlineOrderContext} from '../helpers/OnlineOrderContext';
import {LinearGradient} from "expo-linear-gradient";
import CustomHeader from "../helpers/CustomHeader";
import PlaceholderImage from "../../assets/placeholder.png";
import {Image as ExpoImage} from "expo-image";
import { isDemoMode, demoStockPhoto } from "../demo";
import {CartContext} from '../helpers/CartContext';
import {generateObjectId} from "../helpers/objectId";
import {priceFormatter} from "../helpers/numbers";
import {isColorDark} from "../helpers/colors";
import LottieView from "lottie-react-native";
import dataLoadingAnimation from "../../assets/animations/data-loading.json";
import noDataAnimation from "../../assets/animations/no-item-found.json";
import {SystemContext} from '../helpers/SystemContext';
import Observables from "@happy-gastro/global-observables";
import Icon from 'react-native-vector-icons/FontAwesome';
import {TableContext} from '../helpers/TableContext';
import {OrdersContext} from "../helpers/OrdersContext";
import {Ionicons} from "@expo/vector-icons";
import {useRoute} from "@react-navigation/native";
import {useTranslation} from "react-i18next";
import ExtrasModal from "../components/ExtrasModal";
import {VipCardContext} from "../helpers/VipCardContext";
import CustomTooltip from "../helpers/CustomTooltip";
import {TourContext} from "../helpers/TourContext";
import {useTheme} from '../helpers/ThemeContext';
import {useThemedStyles} from '../helpers/useThemedStyles';
import {fetchMostSoldFoods, getManagementScope} from '../helpers/managementApi';
import {foodBelongsToCategory} from '../helpers/foodCategories';
import {posTileMaxWidth, resolvePosGridColumns, usePosLayout} from '../helpers/posLayout';
import {haptics} from '../helpers/haptics';
import SizePickerSheet from '../components/pos/SizePickerSheet';

const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return value.$oid || value._id || value.id || '';
    }
    return '';
};


const EventBox = React.memo((props) => {
    const { ev, onClickEvent, ticketCount, styles, compact } = props;

    const setEvent = () => {
        haptics.select();
        props.onClickEvent(ev);
    };
    const isDark = isColorDark(ev.color || '#fefe');


    return (
        <Pressable
            accessibilityRole="button"
            style={{ width: '100%', backgroundColor: ev.color ? ev.color : '#fff' }}
            onPress={() => setEvent()}
        >
            {Platform.OS !== 'web' ? (
                <Image
                    height={80}
                    resizeMode={'cover'}
                    resizeMethod={'auto'}
                    style={[styles.productImage, { width: '100%', height: 80, resizeMode: 'cover' }]}
                    source={PlaceholderImage}
                />
            ) : (
                <img width={'100%'} src="https://placehold.co/100x102" style={styles.productImage} alt="product" />
            )}
            <Text numberOfLines={2} style={[styles.productName, compact && styles.productNameCompact, { color: isDark ? '#fff' : '#000' }]}>{ev.name}</Text>
            {ticketCount > 0 && (
                <View style={[styles.badgeContainer(true), compact && styles.badgeContainerCompact]}>
                    <Text style={[styles.badgeText, compact && styles.badgeTextCompact]}>{ticketCount}</Text>
                </View>
            )}
        </Pressable>
    );
});
const EventsList = (props) => {
    const { styles } = props;
    const { cartItems } = useContext(CartContext);
    const { eventColumnCount } = useContext(SystemContext);
    const { width, isCompact } = usePosLayout();
    const columns = resolvePosGridColumns(eventColumnCount, width);
    const tileStyle = [styles.productBox, { maxWidth: posTileMaxWidth(columns) }];

    const getTicketCountForEvent = (event) => {
        return cartItems.reduce((total, item) => {
            if (event?.tickets?.some(ticket => ticket.id === item.itemId)) {
                return total + item?.quantity;
            }
            return total;
        }, 0);
    };

    const renderItem = ({ item, index }) => {
        const ticketCount = getTicketCountForEvent(item);
        return (
            <View style={tileStyle}>
                <EventBox ev={item} onClickEvent={props.onClickEvent} ticketCount={ticketCount} styles={styles} compact={isCompact} />
            </View>
        );
    };

    return (
        <View style={{ flex: 1 }}>
            <FlatList
                data={props.events}
                accessibilityRole={'list'}
                renderItem={renderItem}
                keyExtractor={item => item.id}
                keyboardShouldPersistTaps="always"
                numColumns={columns}
                key={columns + "columnsEvent"}
                contentContainerStyle={styles.container}
                initialNumToRender={10}
                maxToRenderPerBatch={5}
                windowSize={5}
                removeClippedSubviews={Platform.OS === 'android'}
            />
        </View>
    );
};

const TicketRow = React.memo((props) => {
    const { ticket, onClickEvent, styles } = props;
    const { cartItems } = useContext(CartContext);
    const isDark = isColorDark(ticket.color || '#fefe');
    const [tempCount, setTempCount] = useState(0);

    const ticketCount = useMemo(() => {
        let count = 0;
        for (let i = 0; i < cartItems.length; i++) {
            if (cartItems[i].itemId === ticket.id) count += cartItems[i].quantity;
        }
        return count;
    }, [cartItems, ticket.id]);

    const prevTicketCountRef = React.useRef(ticketCount);
    if (prevTicketCountRef.current !== ticketCount) {
        prevTicketCountRef.current = ticketCount;
        if (tempCount !== 0) setTempCount(0);
    }

    const setTicket = () => {
        haptics.tap();
        setTempCount(tempCount + 1);
        onClickEvent(ticket);
    };

    return (
        <TouchableOpacity
            accessibilityRole="button"
            style={[styles.ticketRow, { backgroundColor: ticket.color || '#fefe' }]}
            onPress={setTicket}
        >
            <Text style={[styles.ticketName, { color: isDark ? '#fff' : '#000' }]}>
                {ticket.name}
            </Text>
            {(ticketCount + tempCount) > 0 && (
                <View style={styles.badgeContainer(false)}>
                    <Text style={styles.badgeText}>{ticketCount + tempCount}</Text>
                </View>
            )}
            <Text style={[styles.ticketPrice, { color: isDark ? '#fff' : '#000' }]}>
                {priceFormatter(ticket.price)}
            </Text>
        </TouchableOpacity>
    );
});
const TicketList = (props) => {
    const { styles } = props;
    const { t } = useTranslation();
    const Breadcrumb = () => (
        <Pressable accessibilityRole="link" onPress={props.goBack} style={styles.breadcrumbContainer}>
            <Text style={styles.breadcrumbText}>{'<'} {props?.currentEvent?.name || t('tickets')}</Text>
        </Pressable>
    );

    return (
        <View style={{ flex: 1 }}>
            <FlatList
                accessibilityRole="list"
                data={props.events.find(e => e.id === props.currentEvent.id).tickets}
                renderItem={({ item }) => <TicketRow ticket={item} onClickEvent={() => props.onClickTicket(item)} styles={styles} />}
                keyExtractor={item => item.id}
                keyboardShouldPersistTaps="always"
                numColumns={1}
                contentContainerStyle={[styles.container, { marginTop: 12 }]}
                initialNumToRender={15}
                maxToRenderPerBatch={10}
                windowSize={5}
                removeClippedSubviews={Platform.OS === 'android'}
            />
        </View>
    );
};

const CategoryBox = React.memo((props) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';
    const { category, onClickCategory, itemCount, styles, compact } = props;
    const { theme, isDark: darkMode } = useTheme();
    const setCategory = () => {
        haptics.select();
        props.onClickCategory(category);
    };
    // Uncoloured groups follow the app theme (they used to be a hard-coded white
    // tile with black text — a glaring block in dark mode).
    const hasColor = Boolean(category?.color);
    const isDark = hasColor ? isColorDark(category.color) : darkMode;
    const tileColor = hasColor ? category.color : theme.colors.card;
    const placeholderColor = hasColor ? category.color : (darkMode ? 'rgba(255,255,255,0.06)' : '#ece9f1');
    // Demo mode: fill photoless category tiles with a deterministic stock photo so
    // the POS grid looks stocked (real menu names/prices are untouched).
    const categoryPhoto = category?.photo
        || (isDemoMode() ? demoStockPhoto(category?._id || category?.name?.hu, 'category') : null);

    return (
        <Pressable
            accessibilityRole="button"
            style={{ width: '100%', backgroundColor: tileColor }}
            onPress={() => setCategory()}
        >
            {categoryPhoto ? (
                Platform.OS !== 'web' ? (
                    // expo-image with a memory-disk cache: category photos are
                    // decoded once and cached, so scrolling/re-rendering the POS
                    // catalog grid never re-decodes or refetches them (kills the
                    // catalog-scroll jank plain RN Image caused).
                    <ExpoImage
                        cachePolicy="memory-disk"
                        contentFit="cover"
                        transition={150}
                        style={[styles.productImage, { width: '100%', height: 60 }]}
                        source={{ uri: categoryPhoto }}
                    />
                ) : (
                    <img width={'100%'} src={categoryPhoto} style={styles.productImage} alt="category" />
                )
            ) : (
                // Clean neutral placeholder tinted by the category colour, instead of one
                // shared product-looking image repeated across every group tile. (SVC-007)
                <View style={[styles.productImage, { width: '100%', height: 60, alignItems: 'center', justifyContent: 'center', backgroundColor: placeholderColor }]}>
                    <Ionicons name="restaurant-outline" size={26} color={hasColor ? (isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.4)') : '#F97316'} />
                </View>
            )}
            <Text numberOfLines={2} style={[styles.productName, compact && styles.productNameCompact, { color: isDark ? '#fff' : '#000', height: compact ? 44 : 40, alignItems: 'center', alignSelf: 'center', display: 'flex' }]}>
                {category?.name?.[currentLocale] || category?.name?.hu || t('category_name')}
            </Text>
            {itemCount > 0 && (
                <View style={[styles.badgeContainer(true), compact && styles.badgeContainerCompact]}>
                    <Text style={[styles.badgeText, compact && styles.badgeTextCompact]}>{itemCount}</Text>
                </View>
            )}
        </Pressable>
    );
});

const CategoryList = (props) => {
    const { styles } = props;
    const { cartItems } = useContext(CartContext);
    const { categoryColumnCount } = useContext(SystemContext);
    const { width, isCompact } = usePosLayout();
    const columns = resolvePosGridColumns(categoryColumnCount, width);
    const tileStyle = [styles.productBox, { maxWidth: posTileMaxWidth(columns) }];

    const getItemCountForCategory = (categoryId) => {
        return cartItems.reduce((total, item) => {
            // Multi-category membership (categories when present, else the
            // legacy scalar), plus the existing parent/child rollup.
            if (foodBelongsToCategory(item, categoryId) || item?.category?.parent === categoryId) {
                return total + item.quantity;
            }
            return total;
        }, 0);
    };

    const renderItem = ({ item, index }) => {
        const itemCount = getItemCountForCategory(item._id);
        return (
            <View style={tileStyle}>
                <CategoryBox category={item} onClickCategory={props.onClickCategory} itemCount={itemCount} styles={styles} compact={isCompact} />
            </View>
        );
    };

    return (
        <View style={{ flex: 1 }}>
            <FlatList
                data={props.categories}
                accessibilityRole={'list'}
                renderItem={renderItem}
                keyExtractor={item => item._id}
                keyboardShouldPersistTaps="always"
                numColumns={columns}
                key={columns + "columnsCat"}
                contentContainerStyle={styles.container}
                initialNumToRender={15}
                maxToRenderPerBatch={10}
                windowSize={5}
                removeClippedSubviews={Platform.OS === 'android'}
            />
        </View>
    );
};

// Instant pressed feedback for product taps. Taps are coalesced for a short
// window before the line lands in the cart, so without feedback cashiers read
// the tap as missed and press again.
// Taps on the same product closer together than this merge into one cart line.
const TAP_MERGE_WINDOW_MS = 350;
const FOOD_PRESS_RIPPLE = { color: 'rgba(0, 0, 0, 0.15)' };
const FOOD_PRESSED_STYLE = { opacity: 0.7 };

const FoodRow = React.memo((props) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';
    const { food, onClickFood, styles } = props;
    const isDark = isColorDark(food?.color || '#fefe');
    const [tempCount, setTempCount] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedFood, setSelectedFood] = useState(null);

    // Cart quantity comes from the parent list (see buildCartCountByItemId) so a
    // cart change only re-renders the tiles whose count actually changed.
    const itemCount = props.itemCount || 0;

    const prevItemCountRef = React.useRef(itemCount);
    if (prevItemCountRef.current !== itemCount) {
        prevItemCountRef.current = itemCount;
        if (tempCount !== 0) setTempCount(0);
    }

    const setTicket = () => {
        if (food.sizes.length > 1) {
            haptics.select();
            setSelectedFood(food);
            setModalVisible(true);
        } else {
            haptics.tap();
            setTempCount(tempCount + 1);
            onClickFood({ ...food, price: food.sizes[0].price, unit: food.sizes[0].unit });
        }
    };

    const handleSizeSelect = (size, index) => {
        onClickFood({ ...selectedFood, price: size.price, unit: size.unit, sizeIndex: index });
        setModalVisible(false);
        setSelectedFood(null);
    };

    return (
        <Pressable
            accessibilityRole="button"
            android_ripple={FOOD_PRESS_RIPPLE}
            style={({ pressed }) => [styles.ticketRow, { backgroundColor: food.color || '#fefe' }, pressed && FOOD_PRESSED_STYLE]}
            onPress={setTicket}
        >
            {food?.description?.[currentLocale] || food?.description?.hu || food?.description?.en ?
                <View style={{ maxWidth: "80%", flexWrap: 'wrap' }}>
                    <Text style={[styles.ticketName, { color: isDark ? '#fff' : '#000' }]}>
                        {food?.name?.[currentLocale] || food?.name?.hu || ""}
                    </Text>
                    <Text style={styles.descriptionText}>
                        {food?.description?.[currentLocale] || food?.description?.hu || food?.description?.en}
                    </Text>
                </View>
                :
                <Text style={[styles.ticketName, { color: isDark ? '#fff' : '#000', marginVertical: 8 }]}>
                    {food?.name?.[currentLocale] || food?.name?.hu || ""}
                </Text>
            }
            {(itemCount + tempCount) > 0 && (
                <View style={styles.badgeContainer(false)}>
                    <Text style={styles.badgeText}>{itemCount + tempCount}</Text>
                </View>
            )}
            <Text style={[styles.ticketPrice, { color: isDark ? '#fff' : '#000' }]}>
                {priceFormatter(food?.sizes[0]?.price || 0)}
            </Text>
            <SizePickerSheet
                visible={modalVisible}
                food={selectedFood}
                onSelect={handleSizeSelect}
                onClose={() => {
                    setModalVisible(false);
                    setSelectedFood(null);
                }}
            />
        </Pressable>
    );
});

const FoodBox = React.memo((props) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';
    const { food, onClickFood, styles, compact } = props;
    const isDark = isColorDark(food?.color || '#fefe');
    const [tempCount, setTempCount] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedFood, setSelectedFood] = useState(null);

    // Cart quantity comes from the parent list (see buildCartCountByItemId) so a
    // cart change only re-renders the tiles whose count actually changed.
    const itemCount = props.itemCount || 0;

    const prevItemCountRef = React.useRef(itemCount);
    if (prevItemCountRef.current !== itemCount) {
        prevItemCountRef.current = itemCount;
        if (tempCount !== 0) setTempCount(0);
    }

    const setFood = () => {
        if (food.sizes.length > 1) {
            haptics.select();
            setSelectedFood(food);
            setModalVisible(true);
        } else {
            haptics.tap();
            setTempCount(tempCount + 1);
            onClickFood({ ...food, price: food.sizes[0].price, unit: food.sizes[0].unit });
        }
    };

    const handleSizeSelect = (size, index) => {
        onClickFood({ ...selectedFood, price: size.price, unit: size.unit, sizeIndex: index });
        setModalVisible(false);
        setSelectedFood(null);
    };

    // Demo mode: fill photoless product tiles with a deterministic stock photo
    // instead of the shared "no image" placeholder (menu names/prices untouched).
    const NO_IMG = 'https://happy-releases.fra1.cdn.digitaloceanspaces.com/Uploads/Images/food-no-img.jpg';
    const foodImageUri = food.photo || food.image
        || (isDemoMode() ? demoStockPhoto(food?._id || food?.name?.hu, 'food') : NO_IMG);

    return (
        <Pressable
            accessibilityRole="button"
            android_ripple={FOOD_PRESS_RIPPLE}
            style={({ pressed }) => [{ width: '100%', backgroundColor: food?.color ? food?.color : '#fff', position: 'relative' }, pressed && FOOD_PRESSED_STYLE]}
            onPress={setFood}
        >
            {/* Price tag in top right corner */}
            <View style={styles.priceTag}>
                <Text style={[styles.priceTagText, { color: isDark ? '#fff' : '#000' }]}>
                    {priceFormatter(food?.sizes[0]?.price || 0)}
                </Text>
            </View>

            {Platform.OS !== 'web' ? (
                // expo-image (memory-disk cache) — disk-cached, decode offloaded,
                // so the food-catalog scroll grid never re-decodes/refetches
                // remote food photos on every render.
                <ExpoImage
                    cachePolicy="memory-disk"
                    contentFit="cover"
                    transition={150}
                    style={[styles.productImage, compact && styles.productImageCompact]}
                    source={{ uri: foodImageUri }}
                />
            ) : (
                <img src={foodImageUri} style={{ ...styles.productImage, ...(compact ? styles.productImageCompact : null) }} alt="food" />
            )}
            <Text numberOfLines={2} style={[styles.productName, compact && styles.productNameCompact, { color: isDark ? '#fff' : '#000' }]}>
                {food?.name?.[currentLocale] || food?.name?.hu || ""}
            </Text>
            {(itemCount + tempCount) > 0 && (
                <View style={[styles.badgeContainer(true), compact && styles.badgeContainerCompact]}>
                    <Text style={[styles.badgeText, compact && styles.badgeTextCompact]}>{itemCount + tempCount}</Text>
                </View>
            )}
            <SizePickerSheet
                visible={modalVisible}
                food={selectedFood}
                onSelect={handleSizeSelect}
                onClose={() => {
                    setModalVisible(false);
                    setSelectedFood(null);
                }}
            />
        </Pressable>
    );
});

/**
 * Order a filtered food list by the active sort mode. 'mostSold' ranks by the
 * per-food sold quantity (highest first) supplied by the backend most-sold
 * endpoint; every mode falls back to the manual `order` field and finally to
 * the incoming (backend menu-order) sequence, so foods missing a metric keep
 * their catalog position and never disappear or crash the grid.
 *
 * @param {Object[]} list Already-filtered foods.
 * @param {('order'|'mostSold')} sortMode Active sort mode.
 * @param {Object<string, number>} soldById Map of foodId -> quantity sold.
 * @returns {Object[]} A new, sorted array.
 */
const sortFoods = (list, sortMode = 'order', soldById = {}) => {
    const indexed = Array.isArray(list) ? list.map((food, index) => ({ food, index })) : [];
    const orderVal = (f) => (typeof f?.order === 'number' ? f.order : Number.MAX_SAFE_INTEGER);
    const soldVal = (f) => {
        const q = soldById ? soldById[f?._id] : undefined;

        return typeof q === 'number' ? q : -1;
    };

    indexed.sort((a, b) => {
        if (sortMode === 'mostSold') {
            const soldDiff = soldVal(b.food) - soldVal(a.food);

            if (soldDiff !== 0) {
                return soldDiff;
            }
        }

        const orderDiff = orderVal(a.food) - orderVal(b.food);

        if (orderDiff !== 0) {
            return orderDiff;
        }

        return a.index - b.index; // stable: preserve backend menu order
    });

    return indexed.map((x) => x.food);
};

const FoodList = (props) => {
    const { styles, theme } = props;
    const { t } = useTranslation();
    const { productDisplayMode, productColumnCount } = useContext(SystemContext);
    const { cartItems } = useContext(CartContext);
    const cartCountByItemId = useMemo(() => buildCartCountByItemId(cartItems), [cartItems]);
    const { width, isCompact } = usePosLayout();
    const columns = resolvePosGridColumns(productColumnCount, width);
    const tileStyle = [styles.productBox, { maxWidth: posTileMaxWidth(columns) }];
    const foods = useMemo(() => {
        const sourceFoods = Array.isArray(props.foods) ? props.foods : [];

        const filtered = props.isSearched
            ? sourceFoods.filter((food) => food.visible === true && food.enabled === true)
            : sourceFoods.filter((food) => {
                // Multi-category foods appear under every category they belong to
                // (categories membership when present, else the legacy scalar).
                return foodBelongsToCategory(food, props.currentCategory?._id) &&
                    food.visible === true &&
                    food.enabled === true;
            });

        return sortFoods(filtered, props.sortMode, props.mostSoldById);
    }, [props.foods, props.isSearched, props.currentCategory?._id, props.sortMode, props.mostSoldById]);

    const hasFoods = foods.length > 0;

    const renderGridItem = ({ item, index }) => {
        return (
            <View style={tileStyle}>
                <FoodBox food={item} itemCount={getCartCount(cartCountByItemId, item._id)} onClickFood={props.onClickFood} styles={styles} compact={isCompact} />
            </View>
        );
    };

    return (
        <View style={{ flex: 1 }}>
            {!hasFoods && <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', marginBottom: 60 }}>
                <LottieView
                    source={noDataAnimation}
                    autoPlay
                    // Play once: an endlessly looping idle animation (visible most of
                            // the day on the tablet cart panel) costs frames on low-end devices.
                            loop={false}
                    style={styles.lottie}
                    renderMode={'AUTOMATIC'}
                    resizeMode={'center'}
                    enableSafeModeAndroid={true}
                />
                <Text style={{ fontSize: 22, color: theme.colors.text, marginTop: 10 }}>{t('this_group_is_empty')}</Text>
            </View>}
            {hasFoods && productDisplayMode === 'list' && (
                <FlatList
                    accessibilityRole="list"
                    data={foods}
                    renderItem={({ item }) => <FoodRow food={item} itemCount={getCartCount(cartCountByItemId, item._id)} onClickFood={props.onClickFood} styles={styles} />}
                    keyExtractor={item => item._id}
                    keyboardShouldPersistTaps="always"
                    numColumns={1}
                    contentContainerStyle={[styles.container, { marginTop: 12 }]}
                    initialNumToRender={15}
                    maxToRenderPerBatch={10}
                    windowSize={5}
                    removeClippedSubviews={Platform.OS === 'android'}
                />
            )}
            {hasFoods && productDisplayMode === 'grid' && (
                <FlatList
                    data={foods}
                    accessibilityRole={'list'}
                    renderItem={renderGridItem}
                    keyExtractor={item => item._id}
                    keyboardShouldPersistTaps="always"
                    numColumns={columns}
                    key={columns + "columnsProduct"}
                    contentContainerStyle={styles.container}
                    initialNumToRender={10}
                    maxToRenderPerBatch={5}
                    windowSize={5}
                    removeClippedSubviews={Platform.OS === 'android'}
                />
            )}
        </View>
    );
};

const SaleScreen = ({ navigation, numColumns }) => {
    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';
    const { theme, isDark } = useTheme();
    const styles = useThemedStyles(buildStyles);
    const {
        defaultView, fetchSystemData, loaded, events = [], categories = [], foods = [], eventColumnCount, categoryColumnCount,
        selectedEvent, setSelectedEvent, selectedCategory, setSelectedCategory, foodSortMode = 'order'
    } = useContext(SystemContext);
    const { orderData, orderType, resetOnlineOrder } = useContext(OnlineOrderContext);
    const { orders } = useContext(OrdersContext);
    const { nextStep, endTour, currentStep } = useContext(TourContext);
    const { addToCart, setCartItems, removeFromCart, cartItems, alreadySentCartItems, setAlreadySentCartItems, setDiscountsForWholeCart, cartTotal = 0, cartTotalWithServiceFee = 0, serviceFeeAmount = 0 } = useContext(CartContext);
    const { currentTable, removeTable } = useContext(TableContext);
    const { selectedVipCard, removeVipCard, selectedEmployee, removeEmployee } = useContext(VipCardContext);
    const { isPhone, isPhoneUi } = usePosLayout();
    const [isKeyboardVisible, setKeyboardVisible] = useState(false);

    // Phones only: the sticky cart bar would sit on top of the keyboard while
    // the cashier types a product search, so it steps aside until it closes.
    useEffect(() => {
        if (!isPhone) {
            return undefined;
        }

        const showSub = Keyboard.addListener('keyboardDidShow', () => setKeyboardVisible(true));
        const hideSub = Keyboard.addListener('keyboardDidHide', () => setKeyboardVisible(false));

        return () => {
            showSub.remove();
            hideSub.remove();
        };
    }, [isPhone]);

    // Same payable figure as the Cart tab footer (minus the online delivery
    // fee, which only the Cart screen resolves from the customer's ZIP code).
    const cartSummary = useMemo(() => {
        // `|| 1` mirrors CartContext's price sums, which count a missing quantity as one.
        const pendingCount = (cartItems || []).reduce((sum, item) => sum + (Number(item?.quantity) || 1), 0);
        const sentCount = (alreadySentCartItems || []).reduce((sum, item) => sum + (Number(item?.amount) || 1), 0);

        return {
            count: pendingCount + sentCount,
            total: currentTable && serviceFeeAmount > 0 ? cartTotalWithServiceFee : cartTotal,
        };
    }, [cartItems, alreadySentCartItems, currentTable, serviceFeeAmount, cartTotalWithServiceFee, cartTotal]);

    // Breadcrumb title: smaller and single-line on phones, so a long
    // "All › Parent › Child" path truncates instead of wrapping over the grid.
    const crumbTextStyle = useMemo(() => ({
        fontFamily: "'Poppins', sans-serif",
        fontSize: isPhone ? 18 : 22,
        fontWeight: "900",
        animation: "fadeIn",
        margin: 0,
        color: theme.colors.text,
        flexShrink: 1,
    }), [isPhone, theme.colors.text]);
    const crumbChevronSize = isPhone ? 18 : 24;
    const [showGrid, setShowGrid] = useState(defaultView === "categories" && events.length ? 'events' : 'categories');
    const [searchString, setSearchString] = useState("");
    const [loading, setLoading] = useState(!loaded);
    const [refreshing, setRefreshing] = useState(false);
    const clickCountRef = React.useRef({});
    const [searchResultFoods, setSearchResultFoods] = useState([]);
    const [searchResultEvents, setSearchResultEvents] = useState([]);
    // Map of foodId -> quantity sold, powering the 'most sold' catalog sort.
    // Fetched lazily only when the user picks that sort mode; the sort falls
    // back to menu order/name whenever a food is missing from the map.
    const [mostSoldById, setMostSoldById] = useState({});

    useEffect(() => {
        if (foodSortMode !== 'mostSold') {
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const scope = await getManagementScope();

                if (!scope?.restaurantId) {
                    return;
                }

                const rows = await fetchMostSoldFoods(scope, { period: 'last_30_days', limit: 300 });

                if (cancelled) {
                    return;
                }

                const map = {};

                (Array.isArray(rows) ? rows : []).forEach((row) => {
                    const id = row?.foodId || row?._id;

                    if (id) {
                        map[String(id)] = Number(row?.soldQty) || 0;
                    }
                });
                setMostSoldById(map);
            } catch (error) {
                // Non-fatal: an empty map just leaves the list in menu order.
                console.warn('[SaleScreen] most-sold fetch failed', error?.message);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [foodSortMode]);

    const route = useRoute();
    const userInfo = route.params?.userInfo;
    //const orderType = route.params?.orderType;

    const [extrasModalVisible, setExtrasModalVisible] = useState(false);
    const [extrasModalItem, setExtrasModalItem] = useState(null);
    const [extrasModalCallback, setExtrasModalCallback] = useState(null);


    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        await fetchSystemData({ force: true });
        setRefreshing(false);
    }, [fetchSystemData]);

    /*
    useEffect(() => {
        //food search logic
        const isSearched = searchString?.length > 1;

        if (isSearched && foods) {
            setSearchResultFoods(searchFoodByKeywords(searchString, foods));
        }
        if(searchString.length < 1){
            setSearchResultFoods(foods)
        }

    }, [searchString]);*/

    useEffect(() => {
        Observables.register('systemDataLoaded', () => {
            setLoading(false);
        });
        return () => {
            if (typeof Observables.remove === 'function') {
                Observables.remove('systemDataLoaded');
            }
        };
    }, []);
    useEffect(() => {
        if (currentTable) {
            const currentTableId = normalizeId(currentTable?._id);
            const tableOrder = orders?.find(
                (order) =>
                    !order.storno &&
                    order.status !== "cancelled" &&
                    order.status !== "closed" &&
                    normalizeId(order?.tableId) === currentTableId
            );
            if (tableOrder) {
                setAlreadySentCartItems(Array.isArray(tableOrder?.items) ? tableOrder.items : []);
                if (tableOrder?.discounts?.percentage > 0 || tableOrder?.discounts?.amount > 0) {
                    setDiscountsForWholeCart(tableOrder?.discounts);
                }
            }
        }
    }, [currentTable, orders, setAlreadySentCartItems, setDiscountsForWholeCart]);

    useEffect(() => {
        if (loaded) {
            if (!events?.length > 0) {
                setShowGrid('categories');
            }
        }
    }, [loaded, events]);

    const incrementItemCount = useCallback((item) => {
        const itemId = item._id || item.id;
        const now = Date.now();
        const lastTap = clickCountRef.current[itemId];

        // The first tap adds the line to the cart IMMEDIATELY (it used to wait
        // 350ms for more taps). Further taps on the same product within the
        // window raise that same line's quantity, so rapid taps still become one
        // line with a higher quantity instead of several single lines.
        if (lastTap && now - lastTap.at < TAP_MERGE_WINDOW_MS) {
            lastTap.at = now;
            const { lineId } = lastTap;
            setCartItems((prevItems) => (prevItems.some((line) => line._id === lineId)
                ? prevItems.map((line) => (line._id === lineId
                    ? { ...line, quantity: (Number(line.quantity) || 1) + 1 }
                    : line))
                : [...prevItems, { ...item, quantity: 1, itemId, _id: lineId }]));
        } else {
            const lineId = generateObjectId();
            clickCountRef.current[itemId] = { at: now, lineId };
            addToCart({ ...item, quantity: 1, itemId, _id: lineId });
        }
        setExtrasModalVisible(false)
    }, [addToCart, setCartItems]);

    const handleItemClick = useCallback(async (item) => {
        if (Platform.OS === 'android') {
            Keyboard.dismiss();
        }

        if (item.extras && item.extras.length > 0) {
            const result = await new Promise((resolve) => {
                setExtrasModalVisible(true);
                setExtrasModalItem(item);
                setExtrasModalCallback(resolve);
            });

            if (result) {
                // If the modal was saved, add the item to the cart
                incrementItemCount(item);
            }
        } else {
            incrementItemCount(item);
        }
    }, [incrementItemCount]);

    // POS Hub search / scan hand-off (`posSearch: {query, nonce, scan}`). A scan that
    // resolves to exactly one product goes straight to the cart; anything else lands
    // in the search results. The nonce lets the same code be sent twice in a row.
    const posSearch = route.params?.posSearch;
    const handledPosSearchRef = React.useRef(null);
    useEffect(() => {
        if (!posSearch?.nonce || handledPosSearchRef.current === posSearch.nonce) {
            return;
        }
        handledPosSearchRef.current = posSearch.nonce;

        const query = String(posSearch.query || '').trim();

        if (!query) {
            return;
        }

        if (posSearch.scan) {
            const matches = findFoodsByCode(query, foods);

            if (matches.length === 1) {
                setSearchString('');
                haptics.success();
                handleItemClick(matches[0].food);

                return;
            }
            // A typed name search is not a failed scan: only buzz for camera / wedge codes.
            if (matches.length === 0 && posSearch.source && posSearch.source !== 'manual') {
                haptics.error();
            }
        }
        setSearchString(query);
    }, [posSearch?.nonce, posSearch?.query, posSearch?.scan, posSearch?.source, foods, handleItemClick]);

    // if (loading) {
    //     return (
    //         <LinearGradient
    //             colors={['#EAE5EE', '#FEE6DE', '#EBE5F2']}
    //             style={styles.background}
    //             start={{ x: 0.3, y: 0 }}
    //             end={{ x: 0.7, y: 1 }}
    //         >
    //
    //
    //         </LinearGradient>
    //     );
    // }

    const rootCategories = useMemo(() => categories.filter(category => !category.parent), [categories]);
    const childCategories = useMemo(
        () => categories.filter(category => category.parent === selectedCategory?._id),
        [categories, selectedCategory?._id]
    );

    // The search box renders `searchString` immediately; the result area reads a
    // deferred copy so React can keep typing responsive and filter the catalog
    // in the background on slower devices (results and the list switch-over
    // both follow the deferred value, so there is no empty-state flash).
    const deferredSearchString = useDeferredValue(searchString);

    const searchedFoods = useMemo(() => {
        if (deferredSearchString?.length <= 1) {
            return [];
        }

        // Exact barcode / SKU hits first (name search never sees codes), then names.
        const byCode = findFoodsByCode(deferredSearchString, foods).map((match) => match.food);
        const byName = searchFoodByKeywords(deferredSearchString, foods);

        if (byCode.length === 0) {
            return byName;
        }

        const codeIds = new Set(byCode.map((food) => food._id));

        return [...byCode, ...byName.filter((food) => !codeIds.has(food._id))];
    }, [deferredSearchString, foods]);

    const isQuickReceiptMode = !currentTable && !orderData?.orderType;

    const parentCategory = useMemo(() => {
        if (!selectedCategory?.parent) {
            return null;
        }

        return categories.find((category) => category._id === selectedCategory.parent) || null;
    }, [categories, selectedCategory?.parent]);

    // Phone UI: a quick swipe to the left jumps to the cart (when there is something in it) —
    // the same destination as the sticky cart bar. A Fling only reacts to fast horizontal
    // flicks, so vertical catalog scrolling and tile taps are untouched.
    const hasCartItems = cartSummary.count > 0;
    const flingToCart = useMemo(() => Gesture.Fling()
        .direction(Directions.LEFT)
        .enabled(isPhoneUi && hasCartItems)
        .runOnJS(true)
        .onEnd((_event, success) => {
            if (success) {
                haptics.medium();
                navigation.navigate('Cart');
            }
        }), [isPhoneUi, hasCartItems, navigation]);

    return (
        <GestureDetector gesture={flingToCart}>
        <LinearGradient
            colors={isDark ? [theme.colors.background, theme.colors.card, theme.colors.background] : ['#EAE5EE', '#FEE6DE', '#EBE5F2']}
            style={styles.background}
            start={{ x: 0.3, y: 0 }}
            end={{ x: 0.7, y: 1 }}
        >
            {!loaded ? <View style={[styles.container, { flex: 1, alignContent: 'center', alignItems: 'center' }]}>
                <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', marginTop: 60 }}>
                    <LottieView
                        source={dataLoadingAnimation}
                        autoPlay
                        loop={true}
                        style={styles.lottie}
                        renderMode={'AUTOMATIC'}
                        resizeMode={'center'}
                        enableSafeModeAndroid={true}
                    />
                    <Text style={{ fontSize: 22, color: theme.colors.text, marginTop: 10 }}>{t('data_loading')}</Text>
                </View>
            </View> : <ScrollView
                style={{ width: '100%', flex: 1, padding: 0, margin: 0, height: '100%', paddingBottom: 30 }}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
                keyboardShouldPersistTaps="always"
            >
                <View>
                    <CustomHeader title={t('sales')} searchString={searchString} setSearchString={setSearchString} setSelectedCategory={setSelectedCategory} />
                </View>
                <View style={[styles.breadcrumbContainer, { backgroundColor: 'transparent' }]}>
                    <TouchableOpacity style={styles.breadcrumbButton} activeOpacity={1}>
                        {((showGrid !== "events" && selectedCategory) || (showGrid === "events" && selectedEvent)) && <Icon name={'chevron-left'} size={30} color={theme.colors.text} style={styles.leftIcon} onPress={() => {
                            if (showGrid === "events") {
                                setSelectedEvent(null);
                            } else {
                                setSelectedCategory(null);
                            }
                        }} />}
                        <View style={styles.centerContent}>

                            {!selectedCategory && (<Text
                                numberOfLines={1}
                                style={crumbTextStyle}
                                onPress={() => {
                                    setSelectedCategory(null);
                                }}
                            >
                                {t('all_groups')} ({categories.length})
                            </Text>)}

                            {selectedCategory && (
                                <>
                                    <Text
                                        numberOfLines={1}
                                        style={[crumbTextStyle, { flexShrink: 0 }]}
                                        onPress={() => {
                                            setSelectedCategory(null);
                                        }}
                                    >
                                        {t('all')}
                                    </Text>
                                    <Ionicons name="chevron-forward" size={crumbChevronSize} color={theme.colors.text} />
                                    {parentCategory && (
                                        <Text
                                            numberOfLines={1}
                                            style={crumbTextStyle}
                                            onPress={() => {
                                                setSelectedCategory(parentCategory || null);
                                            }}
                                        >
                                            {parentCategory.name[currentLocale] || parentCategory.name.hu}
                                            <Ionicons name="chevron-forward" size={crumbChevronSize} color={theme.colors.text} />
                                        </Text>
                                    )}
                                    <Text
                                        numberOfLines={1}
                                        style={crumbTextStyle}
                                    >
                                        {selectedCategory.name[currentLocale] || selectedCategory.name.hu}
                                    </Text>
                                </>
                            )}
                        </View>
                        {(showGrid === "categories" && events.length > 0 || showGrid === "events" && categories.length > 0) && <Icon name={'exchange'} size={30} color={theme.colors.text} style={styles.leftIcon}
                            onPress={() => setShowGrid(showGrid === 'events' ? 'categories' : 'events')} />}
                    </TouchableOpacity>
                </View>
                {orderData?.firstName && orderData?.lastName && orderType && (
                    <View style={styles.orangeLine}>
                        <View style={{ flexDirection: "row" }}>
                            <Ionicons
                                name={orderType === 'online' ? 'bicycle-outline' : 'bag-handle-outline'}
                                size={20}
                                color="white"
                                style={{ marginRight: "5px" }}
                            />
                            <Text style={styles.orangeLineText}>{orderData?.firstName} {orderData?.lastName}</Text>
                            <TouchableOpacity onPress={resetOnlineOrder} style={{ paddingLeft: 20, textAlign: 'right' }}>
                                <Ionicons name="trash-outline" size={24} color="rgb(243 104 40)" />
                            </TouchableOpacity>
                        </View>
                        {orderData?.street && orderData?.city &&
                            <View style={{ flexDirection: "row" }}>
                                <Text style={styles.smallAddress}>{orderData?.city}, {orderData?.street}</Text>
                            </View>}
                    </View>
                )}

                {currentTable && (
                    <TouchableOpacity
                        style={styles.tableNameContainer}
                        onPress={() => {
                            removeTable();
                            navigation.navigate("TablesScreen");
                        }}
                    >
                        <Text style={styles.tableNameText}>{currentTable.name}</Text>
                        <Icon name="times" size={20} color="#fff" style={styles.rightIcon} onPress={() => {
                            removeTable();
                            setAlreadySentCartItems([]);
                            navigation.navigate("TablesScreen");
                        }} />
                    </TouchableOpacity>
                )}
                {isQuickReceiptMode && (
                    <View style={styles.quickReceiptContainer}>
                        <Text style={styles.quickReceiptText}>{t('quick_receipt')}</Text>
                    </View>
                )}
                {selectedVipCard && (
                    <View style={styles.vipCardContainer}>
                        {(selectedVipCard?.user?.name?.first.length < 1 && selectedVipCard?.user?.name?.last.length < 1)
                            || (!selectedVipCard?.user?.name && !selectedVipCard?.user?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {selectedVipCard.cardId}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedVipCard?.user?.name?.first} {selectedVipCard?.user?.name?.last}
                            </Text>}
                        <TouchableOpacity style={{ backgroundColor: "#eea63b", paddingHorizontal: 20, paddingVertical: 4 }} onPress={removeVipCard}>
                            <Icon name="times" size={20} color="#fff" />
                        </TouchableOpacity>
                    </View>
                )}
                {selectedEmployee && (
                    <View style={styles.vipCardContainer}>
                        {(selectedEmployee?.name?.first.length < 1 && selectedEmployee?.name?.last.length < 1)
                            || (!selectedEmployee?.name && !selectedEmployee?.name)
                            ?
                            <Text style={styles.vipCardText}>
                                {t('unnamed')}
                            </Text> : <Text style={styles.vipCardText}>
                                {selectedEmployee?.name?.first} {selectedEmployee?.name?.last}
                            </Text>}
                        <TouchableOpacity style={{ backgroundColor: "#eea63b", paddingHorizontal: 20, paddingVertical: 4 }} onPress={removeEmployee}>
                            <Icon name="times" size={20} color="#fff" />
                        </TouchableOpacity>
                    </View>
                )}

                {showGrid === "events" && <View style={styles.container}>
                    {!selectedEvent && deferredSearchString?.length < 2 && <EventsList events={events} numColumns={eventColumnCount || 2} onClickEvent={setSelectedEvent} styles={styles} />}
                    {selectedEvent && deferredSearchString?.length < 2 &&
                        <TicketList events={events} goBack={() => setSelectedEvent(null)} currentEvent={selectedEvent}
                            onClickTicket={(ticket) => handleItemClick(ticket)} styles={styles} />}
                </View>}

                {showGrid === "categories" && <View style={styles.container}>
                    {!selectedCategory && deferredSearchString?.length < 2 && <CustomTooltip
                        isVisible={currentStep === 3}
                        onNext={async () => {
                            await handleItemClick({ ...foods[0], price: foods[0].sizes[0].price, unit: foods[0].sizes[0].unit })
                            nextStep();
                        }}
                        placement="right"
                        module={"default"}
                        onClose={endTour}
                        wrapped={(<CategoryList categories={rootCategories} onClickCategory={setSelectedCategory} styles={styles} />
                        )}
                    />}
                    {selectedCategory && deferredSearchString?.length < 2 && <View style={styles.container}>
                        {childCategories.length > 0 ? (
                            <CategoryList categories={childCategories} onClickCategory={setSelectedCategory} styles={styles} />
                        ) : (
                            <FoodList foods={foods} currentCategory={selectedCategory} isSearched={false} sortMode={foodSortMode} mostSoldById={mostSoldById} goBack={() => setSelectedCategory(null)} onClickFood={handleItemClick} styles={styles} theme={theme} />
                        )}
                    </View>}
                    {deferredSearchString?.length > 1 &&
                        <View style={styles.container}>
                            {searchResultFoods &&
                                <FoodList foods={searchedFoods} isSearched={true} currentCategory={selectedCategory} sortMode={foodSortMode} mostSoldById={mostSoldById} goBack={() => setSearchString("")} onClickFood={handleItemClick} styles={styles} theme={theme} />
                            }

                        </View>
                    }
                </View>}
                <ExtrasModal
                    visible={extrasModalVisible}
                    item={extrasModalItem}
                    onSave={incrementItemCount}
                    onClose={setExtrasModalVisible}
                />
            </ScrollView>}

            {/* Phones only: Sale and Cart are separate tabs there, so keep the
                running total and a one-tap way to the cart on screen while
                ringing items up (the tablet split view shows the cart already). */}
            {loaded && isPhone && !isKeyboardVisible && cartSummary.count > 0 && (
                <TouchableOpacity
                    accessibilityRole="button"
                    accessibilityLabel={`${t('cart')}: ${cartSummary.count} ${t('pcs')}, ${priceFormatter(cartSummary.total)}`}
                    activeOpacity={0.85}
                    style={styles.cartBar}
                    onPress={() => {
                        haptics.tap();
                        navigation.navigate('Cart');
                    }}
                >
                    <View style={styles.cartBarIconWrap}>
                        <Icon name="shopping-basket" size={20} color="#fff" />
                        <View style={styles.cartBarCount}>
                            <Text style={styles.cartBarCountText}>{cartSummary.count}</Text>
                        </View>
                    </View>
                    <Text style={styles.cartBarLabel} numberOfLines={1}>{t('cart')}</Text>
                    <Text style={styles.cartBarTotal} numberOfLines={1}>{priceFormatter(cartSummary.total)}</Text>
                    <Ionicons name="chevron-forward" size={20} color="#fff" />
                </TouchableOpacity>
            )}

        </LinearGradient>
        </GestureDetector>
    );
};

const buildStyles = (theme) => ({
    breadcrumbContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 6,
        backgroundColor: theme.colors.background,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        zIndex: 6,
    },
    tableNameContainer: {
        padding: 6,
        backgroundColor: '#16a6ff',
        borderBottomWidth: 1,
        borderBottomColor: '#ccc',
        alignItems: 'center',
        flexDirection: 'row',
        paddingLeft: 30, paddingRight: 30,
        justifyContent: 'space-between'
    },
    tableNameText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#fff3e9',
    },
    quickReceiptContainer: {
        padding: 6,
        backgroundColor: theme.colors.card,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
        alignItems: 'center',
        flexDirection: 'row',
        paddingLeft: 30,
        paddingRight: 30,
        justifyContent: 'center',
    },
    quickReceiptText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.text,
    },
    breadcrumbButton: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
    },
    leftIcon: {
        marginRight: 10,
    },
    rightIcon: {
        marginLeft: 10,
    },
    centerContent: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    breadcrumbText: {
        fontSize: 20,
        color: theme.colors.text,
        fontWeight: 'bold',
        marginLeft: 10,
        textAlign: 'center'
    },
    container: {
        zIndex: 10,
        flex: 1,
        padding: 6,
        width: '100%'
    },
    background: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 10,
        // No explicit `height` here on purpose. It used to be the module-scope
        // `Dimensions.get('window').height`, captured once at import time, which
        // (a) contradicts `flex: 1` on the same node — two competing height
        // constraints Yoga has to reconcile on every layout pass — and (b) is
        // stale the moment the tablet rotates or the window resizes, since the
        // module is only evaluated once. `flex: 1` alone fills the parent and
        // stays correct in every orientation.
    },
    ticketRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 10,
        marginHorizontal: 10,
        marginBottom: 10,
        borderRadius: 5,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.3,
        shadowRadius: 2,
        elevation: 3,
    },
    ticketName: {
        fontSize: 16,
        fontWeight: 'bold',
    },
    descriptionText: {
        fontSize: 12,
        color: 'grey',
        marginTop: 4,
        flexWrap: 'wrap',
        width: '80%'
    },
    ticketPrice: {
        fontSize: 23,
        fontWeight: 'bold',
    },
    productPrice: {
        fontSize: 22,
        fontWeight: 'bold',
        marginBottom: 8,
    },
    balanceBox: {
        padding: 20,
        backgroundColor: theme.colors.primary,
        borderRadius: 10,
        marginBottom: 20,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 1,
        shadowRadius: 2.84,
        elevation: 20,
    },
    balanceText: {
        fontSize: 24,
        color: '#fff',
        fontWeight: 'bold',
    },
    transactionList: {
        paddingBottom: 20,
    },
    transactionItem: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: 10,
        backgroundColor: theme.colors.card,
        borderRadius: 10,
        marginBottom: 10,
    },
    orangeLine: {
        backgroundColor: '#50A5F1',
        padding: 10,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
    },
    orangeLineText: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: 22,
    },
    smallAddress: {
        color: 'white',
        fontSize: 16,
    },
    transactionDescription: {
        fontSize: 16,
    },
    transactionAmount: {
        fontSize: 16,
        fontWeight: 'bold',
    },
    centeredView: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        marginTop: 22,
    },
    button: {
        marginVertical: 5,
        padding: 12,
        backgroundColor: '#F76E24',
        borderRadius: 10,
    },
    cancelButton: {
        marginTop: 10,
        padding: 12,
        backgroundColor: '#b1b1b1',
        borderRadius: 5,
    },
    buttonText: {
        fontSize: 20,
        color: 'white',
    },
    modalView: {
        margin: 15,
        backgroundColor: "white",
        borderRadius: 20,
        padding: 20,
        paddingHorizontal: 30,
        alignItems: "center",
        shadowColor: "#000",
        shadowOffset: {
            width: 0,
            height: 2,
        },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
    },
    modalText: {
        marginBottom: 15,
        textAlign: "center",
    },
    // Tile maxWidth is set per list from the live column count (posTileMaxWidth).
    productBox: {
        flex: 1,
        margin: 4,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.card,
        borderRadius: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 3,
        elevation: 4,
        overflow: 'hidden',
    },
    fullWidth: {
        flex: 1,
        width: '100%',
    },
    productImage: {
        width: "100%",
        height: 120,
        marginBottom: 0,
        cover: 'cover',
        objectFit: 'cover',
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
    },
    priceTag: {
        position: 'absolute',
        top: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderTopRightRadius: 8,
        borderBottomLeftRadius: 10,
        zIndex: 10,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.3,
        shadowRadius: 1,
        elevation: 3,
    },
    priceTagText: {
        fontSize: 14,
        fontWeight: 'bold',
    },
    productName: {
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        padding: 8,
        height: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    // Small-phone catalog tiles (usePosLayout().isCompact): shorter photo and
    // a tighter 2-line name so a 2–3 column grid shows more items per screen.
    productImageCompact: {
        height: 88,
    },
    productNameCompact: {
        fontSize: 14,
        lineHeight: 18,
        paddingHorizontal: 6,
        paddingVertical: 6,
        height: 48,
    },
    badgeContainerCompact: {
        width: 28,
        height: 28,
        borderRadius: 14,
    },
    badgeTextCompact: {
        fontSize: 15,
    },
    cartBar: {
        alignSelf: 'stretch',
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 64,
        marginHorizontal: 10,
        marginTop: 6,
        marginBottom: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 16,
        backgroundColor: theme.colors.primary,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.2,
        shadowRadius: 6,
        elevation: 6,
    },
    cartBarIconWrap: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cartBarCount: {
        position: 'absolute',
        top: -6,
        right: -8,
        minWidth: 20,
        height: 20,
        paddingHorizontal: 5,
        borderRadius: 999,
        backgroundColor: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cartBarCountText: {
        fontSize: 12,
        fontWeight: '800',
        color: theme.colors.primary,
    },
    cartBarLabel: {
        flex: 1,
        marginLeft: 12,
        fontSize: 16,
        fontWeight: '700',
        color: '#fff',
    },
    cartBarTotal: {
        marginRight: 6,
        fontSize: 18,
        fontWeight: '800',
        color: '#fff',
    },
    vipCardContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary,
        padding: 10,
        borderRadius: 5,
    },
    vipCardText: {
        flex: 1,
        color: 'white',
        fontSize: 16,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    lottie: {
        width: 300,
        height: 300
    },
    clickCountCircle: {
        position: 'absolute',
        top: -10,
        right: -10,
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'red',
        justifyContent: 'center',
        alignItems: 'center',
    },
    clickCountText: {
        color: 'white',
        fontWeight: 'bold',
    },
    badgeContainer: (isBox) => ({
        position: 'absolute',
        top: -3, // Adjust for more precise positioning
        left: isBox ? -3 : -17, // Conditional left position
        backgroundColor: '#F76E24', // Matches the red badge color
        borderRadius: 18, // Smaller size for compact look
        width: 36, // Reduced width
        height: 36, // Reduced height
        alignItems: 'center',
        justifyContent: 'center',
    }),
    badgeText: {
        color: 'white',
        fontWeight: 'bold',
        fontSize: 23, // Compact font size for the number
        textAlign: 'center',
    },
});

export default React.memo(SaleScreen);
